import { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { supabase } from '@/lib/supabase';
import { PdfPreview } from '@/components/PdfPreview';

/**
 * The student's uploaded pages, so a teacher can check the AI transcript against
 * the actual handwriting. Essay questions carry no per-question crop image, so
 * this is the only place the original work is visible during review.
 * PDFs are linked rather than rendered — react-pdf here would pull the whole
 * viewer into a page that is otherwise text-only.
 */
export function OriginalPages({
  paths,
  variant = 'panel',
}: {
  paths: string[];
  /** 'inline' drops the card chrome for side-by-side use next to a transcript */
  variant?: 'panel' | 'inline';
}) {
  const [urls, setUrls] = useState<Array<{ url: string; isPdf: boolean }>>([]);
  const [open, setOpen] = useState(true);
  const inline = variant === 'inline';

  useEffect(() => {
    setUrls(
      paths.map((p) => ({
        url: supabase.storage.from('assignment-images').getPublicUrl(p).data.publicUrl,
        isPdf: p.toLowerCase().endsWith('.pdf'),
      })),
    );
  }, [paths]);

  if (!urls.length) return null;

  const body = (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <ImageIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          學生原稿（{urls.length} 頁）
        </Typography>
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? '收起' : '展開'}
        </Button>
      </Stack>

      {open && (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {urls.map(({ url, isPdf }, i) =>
            isPdf ? (
              <PdfPreview key={url} url={url} width={inline ? 520 : 560} />
            ) : (
              <Box
                key={url}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.50' }}
              >
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={url}
                    alt={`學生原稿第 ${i + 1} 頁`}
                    loading="lazy"
                    style={{ maxWidth: '100%', display: 'block', height: 'auto' }}
                  />
                </a>
              </Box>
            ),
          )}
        </Stack>
      )}
    </>
  );

  return inline ? (
    <Box>{body}</Box>
  ) : (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: open ? 2 : 1.5 }}>{body}</CardContent>
    </Card>
  );
}
